/**
 * Преобразование форматированного текста между редактором и контрактом.
 *
 * Редактор работает на contentEditable, то есть его состояние — DOM. Сервер
 * принимает строго описанный JSON. Эти два представления связывает пара
 * функций ниже, и вся защита от лишнего живёт здесь: сериализатор пропускает
 * только те узлы и оформления, что есть в контракте, а остальное —
 * вставленное из Word, из письма, с другого сайта — отбрасывает, сохраняя
 * текст.
 *
 * Отбрасывать, а не конвертировать, — осознанное решение. Вставка из Word
 * приносит десятки инлайновых стилей и собственных тегов; поддержать их
 * означало бы, что мобильные клиенты должны понимать то же самое.
 *
 * Функции чистые и не зависят от React. Если позже возьмёте готовый редактор
 * (TipTap, ProseMirror), меняется только компонент — этот модуль остаётся
 * точкой стыковки с контрактом.
 */

import { RICHTEXT_VERSION } from '../../../types/marketingPushInterfaces';
import type {
  RichBlock,
  RichMark,
  RichSpan,
  RichTextDocument,
} from '../../../types/marketingPushInterfaces';

const ALLOWED_MARKS: readonly RichMark[] = ['bold', 'italic', 'underline', 'strike', 'link'];

const TAG_TO_MARK: Record<string, RichMark> = {
  B: 'bold',
  STRONG: 'bold',
  I: 'italic',
  EM: 'italic',
  U: 'underline',
  S: 'strike',
  STRIKE: 'strike',
  DEL: 'strike',
  A: 'link',
};

const MARK_TO_TAG: Record<RichMark, string> = {
  bold: 'strong',
  italic: 'em',
  underline: 'u',
  strike: 's',
  link: 'a',
};

export function emptyDocument(): RichTextDocument {
  return { v: RICHTEXT_VERSION, blocks: [] };
}

export function isEmptyDocument(document?: RichTextDocument): boolean {
  return toPlainText(document).trim().length === 0;
}

/** Схемы кроме http и https отбрасываются: `javascript:` в ссылке
 *  уведомления — дыра, и мобильные клиенты такую ссылку не откроют. */
export function isSafeUrl(url: string): boolean {
  return /^https?:\/\//i.test((url || '').trim());
}

/* -------------------------------------------------------------------------- */
/* Документ -> HTML для редактора                                              */
/* -------------------------------------------------------------------------- */

export function documentToHtml(document?: RichTextDocument): string {
  if (!document || !Array.isArray(document.blocks)) return '';
  return document.blocks.map(blockToHtml).join('');
}

function blockToHtml(block: RichBlock): string {
  if (block.type === 'heading') {
    return `<h${block.level}>${spansToHtml(block.spans)}</h${block.level}>`;
  }
  if (block.type === 'list') {
    const tag = block.ordered ? 'ol' : 'ul';
    const items = block.items.map((item) => `<li>${spansToHtml(item)}</li>`).join('');
    return `<${tag}>${items}</${tag}>`;
  }
  // Пустой абзац без <br> в contentEditable не даёт поставить курсор.
  return `<p>${spansToHtml(block.spans) || '<br>'}</p>`;
}

function spansToHtml(spans: RichSpan[]): string {
  return spans.map(spanToHtml).join('');
}

function spanToHtml(span: RichSpan): string {
  let html = escapeHtml(span.text);
  const marks = (span.marks ?? []).filter((mark) => ALLOWED_MARKS.includes(mark));

  marks.forEach((mark) => {
    if (mark === 'link') return; // ссылку вешаем последней, снаружи
    html = `<${MARK_TO_TAG[mark]}>${html}</${MARK_TO_TAG[mark]}>`;
  });
  if (marks.includes('link') && span.href) {
    html = `<a href="${escapeHtml(span.href)}">${html}</a>`;
  }
  return html;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/* -------------------------------------------------------------------------- */
/* HTML редактора -> документ                                                  */
/* -------------------------------------------------------------------------- */

export function htmlToDocument(root: HTMLElement): RichTextDocument {
  const blocks: RichBlock[] = [];
  Array.from(root.childNodes).forEach((node) => collectBlocks(node, blocks));
  return { v: RICHTEXT_VERSION, blocks: blocks.filter(hasContent) };
}

function collectBlocks(node: Node, blocks: RichBlock[]): void {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? '';
    // Текст вне блоков — типовой результат вставки. Терять его нельзя.
    if (text.trim()) blocks.push({ type: 'paragraph', spans: [{ text }] });
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const element = node as HTMLElement;
  const tag = element.tagName.toUpperCase();

  if (tag === 'H1' || tag === 'H2') {
    blocks.push({ type: 'heading', level: 2, spans: extractSpans(element, []) });
    return;
  }
  if (['H3', 'H4', 'H5', 'H6'].includes(tag)) {
    // Уровни ниже третьего контрактом не предусмотрены — сводим к 3.
    blocks.push({ type: 'heading', level: 3, spans: extractSpans(element, []) });
    return;
  }
  if (tag === 'UL' || tag === 'OL') {
    const items = Array.from(element.querySelectorAll(':scope > li')).map((li) =>
      extractSpans(li as HTMLElement, []),
    );
    if (items.length) blocks.push({ type: 'list', ordered: tag === 'OL', items });
    return;
  }
  if (tag === 'BR') return;
  if (tag === 'P' || tag === 'DIV') {
    blocks.push({ type: 'paragraph', spans: extractSpans(element, []) });
    return;
  }
  if (isBlockLike(element)) {
    // Неизвестный блочный тег: разбираем детей, чтобы не потерять текст.
    Array.from(element.childNodes).forEach((child) => collectBlocks(child, blocks));
    return;
  }
  blocks.push({ type: 'paragraph', spans: extractSpans(element, []) });
}

function isBlockLike(element: HTMLElement): boolean {
  return ['SECTION', 'ARTICLE', 'BLOCKQUOTE', 'TABLE', 'TBODY', 'TR', 'TD', 'MAIN'].includes(
    element.tagName.toUpperCase(),
  );
}

/** Обходит инлайновое дерево, накапливая оформление сверху вниз. */
function extractSpans(element: HTMLElement, inherited: RichMark[]): RichSpan[] {
  const spans: RichSpan[] = [];

  Array.from(element.childNodes).forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      if (text) spans.push(makeSpan(text, inherited, findHref(element)));
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const child = node as HTMLElement;
    const tag = child.tagName.toUpperCase();

    if (tag === 'BR') {
      // Перевод строки внутри абзаца контрактом не предусмотрен.
      // Превращаем в пробел, чтобы слова не склеились.
      spans.push(makeSpan(' ', inherited, findHref(element)));
      return;
    }

    const mark = TAG_TO_MARK[tag];
    const next = mark && !inherited.includes(mark) ? [...inherited, mark] : inherited;
    spans.push(...extractSpans(child, next));
  });

  return mergeAdjacent(spans);
}

function findHref(element: HTMLElement): string | undefined {
  const anchor = element.closest('a');
  const href = anchor?.getAttribute('href') ?? '';
  return isSafeUrl(href) ? href : undefined;
}

function makeSpan(text: string, marks: RichMark[], href?: string): RichSpan {
  const span: RichSpan = { text };
  const cleaned = marks.filter((mark) => ALLOWED_MARKS.includes(mark));

  if (cleaned.includes('link') && !href) {
    // Ссылка без адреса — уже не ссылка. Сервер такой span отклонит,
    // поэтому убираем оформление здесь, а не получаем ошибку при сохранении.
    const withoutLink = cleaned.filter((mark) => mark !== 'link');
    if (withoutLink.length) span.marks = sortMarks(withoutLink);
    return span;
  }

  if (cleaned.length) span.marks = sortMarks(cleaned);
  if (cleaned.includes('link') && href) span.href = href;
  return span;
}

function sortMarks(marks: RichMark[]): RichMark[] {
  return Array.from(new Set(marks)).sort();
}

/** Склеивает соседние фрагменты с одинаковым оформлением: без этого текст,
 *  набранный по буквам, даёт десяток спанов. */
function mergeAdjacent(spans: RichSpan[]): RichSpan[] {
  const result: RichSpan[] = [];
  spans.forEach((span) => {
    const previous = result[result.length - 1];
    if (previous && sameFormatting(previous, span)) {
      previous.text += span.text;
      return;
    }
    result.push({ ...span });
  });
  return result.filter((span) => span.text.length > 0);
}

function sameFormatting(a: RichSpan, b: RichSpan): boolean {
  return (
    (a.marks ?? []).join(',') === (b.marks ?? []).join(',') && (a.href ?? '') === (b.href ?? '')
  );
}

function hasContent(block: RichBlock): boolean {
  if (block.type === 'list') {
    return block.items.some((item) => item.some((span) => span.text.trim()));
  }
  return block.spans.some((span) => span.text.trim());
}

/* -------------------------------------------------------------------------- */
/* Плоский текст                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Плоский текст для предпросмотра и счётчиков.
 *
 * Повторяет серверную `to_plain_text`: сервер вычисляет своё значение сам и
 * клиентскому не доверяет, но предпросмотр должен показывать то же, что
 * окажется в базе. Расхождение здесь — источник вопросов «почему в приложении
 * не то, что я видел».
 *
 * Максимально терпима к мусору: вызывается и на старых записях, которые
 * могли быть сохранены другой версией схемы.
 */
export function toPlainText(document?: RichTextDocument): string {
  if (!document || !Array.isArray(document.blocks)) return '';

  const lines: string[] = [];
  document.blocks.forEach((block) => {
    if (!block || typeof block !== 'object') return;
    if (block.type === 'list') {
      (block.items ?? []).forEach((item, index) => {
        const prefix = block.ordered ? `${index + 1}. ` : '• ';
        const text = spansText(item);
        if (text) lines.push(prefix + text);
      });
      return;
    }
    const text = spansText((block as { spans?: RichSpan[] }).spans);
    if (text) lines.push(text);
  });
  return lines.join('\n');
}

function spansText(spans?: RichSpan[]): string {
  if (!Array.isArray(spans)) return '';
  return spans
    .filter((span) => span && typeof span.text === 'string')
    .map((span) => span.text)
    .join('')
    .trim();
}
